import React, { useCallback, useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  Box, TextField, IconButton, List, ListItem, ListItemText, ListItemSecondaryAction,
  Switch, Typography, Divider, Tooltip, FormControl, InputLabel, Select, MenuItem, OutlinedInput, Chip
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import AddIcon from '@mui/icons-material/Add';
import SettingsIcon from '@mui/icons-material/Settings';
import api from '../../api';

/**
 * CarnetMuscuSettingsManager (Dialog)
 *
 * - Affiche listes ateliers/muscles/mappings
 * - Edition simple (rename/hide/delete), mappings multi-select, mode GUIDE/EXPERT
 *
 * Fix : tolerant loading :
 * - si /muscu/settings renvoie des strings (public view), tente ensuite
 *   /muscu/settings/ateliers and /muscu/settings/groupes (auth-only) pour obtenir
 *   les sous-documents complets (avec _id) nécessaires à l'édition.
 *
 * Props: open (bool), onClose (fn)
 */

function EditableItem({ item, onUpdate, onDelete }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(item.name || '');
  const [hidden, setHidden] = useState(!!item.hidden);

  useEffect(() => {
    setValue(item.name || '');
    setHidden(!!item.hidden);
  }, [item]);

  const save = () => {
    if ((value || '').trim().length === 0) return;
    onUpdate({ ...item, name: value.trim(), hidden });
    setEditing(false);
  };

  return (
    <ListItem divider>
      {!editing ? (
        <>
          <ListItemText primary={item.name} secondary={item.hidden ? 'Masqué' : ''} />
          <ListItemSecondaryAction>
            <Tooltip title={item.hidden ? "Afficher" : "Masquer"}>
              <Switch
                checked={!!item.hidden}
                onChange={() => {
                  const newHidden = !hidden;
                  setHidden(newHidden);
                  onUpdate({ ...item, name: item.name, hidden: newHidden });
                }}
                color="primary"
                inputProps={{ 'aria-label': 'masquer' }}
              />
            </Tooltip>
            <IconButton edge="end" onClick={() => setEditing(true)} aria-label="edit">
              <EditIcon />
            </IconButton>
            <IconButton edge="end" onClick={() => onDelete(item)} aria-label="delete">
              <DeleteIcon />
            </IconButton>
          </ListItemSecondaryAction>
        </>
      ) : (
        <>
          <TextField value={value} onChange={e => setValue(e.target.value)} size="small" sx={{ mr: 1 }} />
          <IconButton onClick={save}><SaveIcon /></IconButton>
        </>
      )}
    </ListItem>
  );
}

export default function CarnetMuscuSettingsManager({ open, onClose }) {
  const [ateliers, setAteliers] = useState([]);
  const [muscles, setMuscles] = useState([]);
  const [mappings, setMappings] = useState([]); // { muscle, ateliers: [] }
  const [mode, setMode] = useState('GUIDE');
  const [newAtelier, setNewAtelier] = useState('');
  const [newMuscle, setNewMuscle] = useState('');
  const [loading, setLoading] = useState(false);
  const [savingMappings, setSavingMappings] = useState(false);

  // normalize helpers
  const normalizeAtelierObjects = (arr) => {
    if (!Array.isArray(arr)) return [];
    return arr.map((a, idx) => {
      if (!a) return null;
      if (typeof a === 'string') {
        return { _id: `name:${a}`, name: a, hidden: false, order: idx };
      }
      // object shape may be { name, hidden, order, _id } or others
      return {
        _id: a._id || a.id || `name:${a.name || a}`,
        name: a.name || a.nom || String(a),
        hidden: !!(a.hidden),
        order: (a.order !== undefined ? a.order : (a.order === 0 ? 0 : idx))
      };
    }).filter(Boolean);
  };

  const normalizeMuscleObjects = (arr) => {
    if (!Array.isArray(arr)) return [];
    return arr.map((m, idx) => {
      if (!m) return null;
      if (typeof m === 'string') {
        return { _id: `name:${m}`, name: m, hidden: false, order: idx };
      }
      return {
        _id: m._id || m.id || `name:${m.name || m}`,
        name: m.name || m.nom || String(m),
        hidden: !!(m.hidden),
        order: (m.order !== undefined ? m.order : (m.order === 0 ? 0 : idx))
      };
    }).filter(Boolean);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // primary: call the public endpoint (may return strings or objects)
      const res = await api.get('/muscu/settings');
      const data = res.data || {};
      let loadedAteliers = data.ateliers ?? [];
      let loadedMuscles = data.muscles ?? [];
      let loadedMappings = Array.isArray(data.mappings) ? data.mappings : [];
      const loadedMode = data.mode || 'GUIDE';

      const atelierIsString = Array.isArray(loadedAteliers) && loadedAteliers.length > 0 && typeof loadedAteliers[0] === 'string';
      const muscleIsString = Array.isArray(loadedMuscles) && loadedMuscles.length > 0 && typeof loadedMuscles[0] === 'string';

      if (atelierIsString || muscleIsString || loadedMappings.length === 0) {
        try {
          const [resAteliers, resMuscles, resMappings] = await Promise.allSettled([
            api.get('/muscu/settings/ateliers'),
            api.get('/muscu/settings/groupes'),
            api.get('/muscu/settings/mappings')
          ]);
          if (resAteliers.status === 'fulfilled' && Array.isArray(resAteliers.value.data) && resAteliers.value.data.length > 0) {
            loadedAteliers = resAteliers.value.data;
          }
          if (resMuscles.status === 'fulfilled' && Array.isArray(resMuscles.value.data) && resMuscles.value.data.length > 0) {
            loadedMuscles = resMuscles.value.data;
          }
          if (resMappings.status === 'fulfilled' && Array.isArray(resMappings.value.data?.mappings)) {
            loadedMappings = resMappings.value.data.mappings;
          }
        } catch (err) {
          console.warn('CarnetMuscuSettingsManager: auth endpoints for ateliers/groupes/mappings failed', err);
        }
      }

      const ateliersObjs = normalizeAtelierObjects(loadedAteliers);
      const musclesObjs = normalizeMuscleObjects(loadedMuscles);

      const mappingsNorm = (Array.isArray(loadedMappings) ? loadedMappings.map(m => ({
        muscle: (m && (m.muscle || m.name)) ? (m.muscle || m.name) : String(m?.muscle || m),
        ateliers: Array.isArray(m?.ateliers) ? m.ateliers.map(a => String(a)) : []
      })) : []);

      setAteliers(ateliersObjs);
      setMuscles(musclesObjs);
      setMappings(mappingsNorm);
      setMode(loadedMode || 'GUIDE');
    } catch (err) {
      console.error('Erreur chargement settings muscu', err);
      setAteliers([]); setMuscles([]); setMappings([]); setMode('GUIDE');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  // ----- CRUD ateliers / muscles -----
  const addAtelier = async () => {
    const name = (newAtelier || '').trim();
    if (!name) return;
    try {
      const res = await api.post('/muscu/settings/atelier', { name });
      setAteliers(Array.isArray(res.data.ateliers) ? normalizeAtelierObjects(res.data.ateliers) : ateliers);
      setNewAtelier('');
    } catch (err) {
      console.error('Erreur ajout atelier', err);
      alert(err.response?.data?.error || 'Erreur ajout atelier');
    }
  };

  const addMuscle = async () => {
    const name = (newMuscle || '').trim();
    if (!name) return;
    try {
      const res = await api.post('/muscu/settings/muscle', { name });
      setMuscles(Array.isArray(res.data.muscles) ? normalizeMuscleObjects(res.data.muscles) : muscles);
      setNewMuscle('');
    } catch (err) {
      console.error('Erreur ajout muscle', err);
      alert(err.response?.data?.error || 'Erreur ajout muscle');
    }
  };

  const updateAtelier = async (it) => {
    try {
      if (!it._id || String(it._id).startsWith('name:')) {
        const res = await api.post('/muscu/settings/atelier', { name: it.name, hidden: !!it.hidden });
        setAteliers(Array.isArray(res.data.ateliers) ? normalizeAtelierObjects(res.data.ateliers) : ateliers);
        return;
      }
      const res = await api.put(`/muscu/settings/atelier/${it._id}`, { name: it.name, hidden: !!it.hidden });
      setAteliers(Array.isArray(res.data.ateliers) ? normalizeAtelierObjects(res.data.ateliers) : ateliers);
    } catch (err) {
      console.error('Erreur maj atelier', err);
      alert(err.response?.data?.error || 'Erreur mise à jour atelier');
    }
  };

  const updateMuscle = async (it) => {
    try {
      if (!it._id || String(it._id).startsWith('name:')) {
        const res = await api.post('/muscu/settings/muscle', { name: it.name, hidden: !!it.hidden });
        setMuscles(Array.isArray(res.data.muscles) ? normalizeMuscleObjects(res.data.muscles) : muscles);
        return;
      }
      const res = await api.put(`/muscu/settings/muscle/${it._id}`, { name: it.name, hidden: !!it.hidden });
      setMuscles(Array.isArray(res.data.muscles) ? normalizeMuscleObjects(res.data.muscles) : muscles);
    } catch (err) {
      console.error('Erreur maj muscle', err);
      alert(err.response?.data?.error || 'Erreur mise à jour muscle');
    }
  };

  const deleteAtelier = async (it) => {
    if (!window.confirm(`Supprimer l'atelier "${it.name}" ?`)) return;
    try {
      if (!it._id || String(it._id).startsWith('name:')) {
        const next = mappings.map(m => ({ ...m, ateliers: (m.ateliers || []).filter(a => a !== it.name) }));
        setMappings(next);
        setAteliers(prev => prev.filter(a => a.name !== it.name));
        await api.put('/muscu/settings/mappings', { mappings: next });
        return;
      }
      const res = await api.delete(`/muscu/settings/atelier/${it._id}`);
      setAteliers(Array.isArray(res.data.ateliers) ? normalizeAtelierObjects(res.data.ateliers) : ateliers.filter(a => a._id !== it._id));
      const next = mappings.map(m => ({ ...m, ateliers: (m.ateliers || []).filter(a => a !== it.name) }));
      setMappings(next);
      await api.put('/muscu/settings/mappings', { mappings: next });
    } catch (err) {
      console.error('Erreur suppression atelier', err);
      alert(err.response?.data?.error || 'Erreur suppression atelier');
    }
  };

  const deleteMuscle = async (it) => {
    if (!window.confirm(`Supprimer le muscle "${it.name}" ?`)) return;
    try {
      if (!it._id || String(it._id).startsWith('name:')) {
        const next = mappings.filter(m => m.muscle !== it.name);
        setMappings(next);
        setMuscles(prev => prev.filter(m => m.name !== it.name));
        await api.put('/muscu/settings/mappings', { mappings: next });
        return;
      }
      const res = await api.delete(`/muscu/settings/muscle/${it._id}`);
      setMuscles(Array.isArray(res.data.muscles) ? normalizeMuscleObjects(res.data.muscles) : muscles.filter(m => m._id !== it._id));
      const next = mappings.filter(mp => mp.muscle !== it.name);
      setMappings(next);
      await api.put('/muscu/settings/mappings', { mappings: next });
    } catch (err) {
      console.error('Erreur suppression muscle', err);
      alert(err.response?.data?.error || 'Erreur suppression muscle');
    }
  };

  // ----- Mode -----
  const handleSetMode = async (newMode) => {
    try {
      await api.put('/muscu/settings/mode', { mode: newMode });
      setMode(newMode);
    } catch (err) {
      console.error('Erreur set mode', err);
      alert(err.response?.data?.error || 'Erreur lors du changement de mode');
    }
  };

  // ----- Mappings -----
  const handleChangeMapping = (muscleName, ateliersSelected) => {
    setMappings(prev => {
      const next = prev.slice();
      const idx = next.findIndex(m => m.muscle === muscleName);
      if (idx === -1) {
        next.push({ muscle: muscleName, ateliers: ateliersSelected });
      } else {
        next[idx] = { ...next[idx], ateliers: ateliersSelected };
      }
      return next;
    });
  };

  const saveMappings = async () => {
    setSavingMappings(true);
    try {
      const res = await api.put('/muscu/settings/mappings', { mappings });
      setMappings(Array.isArray(res.data?.mappings) ? res.data.mappings : mappings);
      alert('Mappings enregistrés');
    } catch (err) {
      console.error('Erreur save mappings', err);
      alert(err.response?.data?.error || 'Erreur sauvegarde mappings');
    } finally {
      setSavingMappings(false);
    }
  };

  const deleteMapping = async (muscleName) => {
    if (!window.confirm(`Supprimer le mapping pour "${muscleName}" ?`)) return;
    try {
      const next = mappings.filter(m => m.muscle !== muscleName);
      setMappings(next);
      await api.put('/muscu/settings/mappings', { mappings: next });
      alert('Mapping supprimé');
    } catch (err) {
      console.error('Erreur suppression mapping', err);
      alert(err.response?.data?.error || 'Erreur suppression mapping');
    }
  };

  const resetDefaults = async () => {
    if (!window.confirm('Réinitialiser les ateliers / muscles / mappings aux valeurs par défaut (écrase la config actuelle) ?')) return;
    try {
      const res = await api.post('/muscu/settings/reset-defaults');
      const data = res.data || {};
      setAteliers(Array.isArray(data.ateliers) ? normalizeAtelierObjects(data.ateliers) : []);
      setMuscles(Array.isArray(data.muscles) ? normalizeMuscleObjects(data.muscles) : []);
      setMappings(Array.isArray(data.mappings) ? data.mappings : []);
      setMode(data.mode || 'GUIDE');
      alert('Valeurs par défaut appliquées');
    } catch (err) {
      console.error('Erreur reset defaults', err);
      alert(err.response?.data?.error || 'Erreur réinitialisation par défaut');
    }
  };

  const atelierNames = ateliers.map(a => a.name);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Paramètres du carnet muscu</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="subtitle2">Mode</Typography>
            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
              <Button
                variant={mode === 'GUIDE' ? 'contained' : 'outlined'}
                onClick={() => handleSetMode('GUIDE')}
              >
                Mode GUIDÉ
              </Button>
              <Button
                variant={mode === 'EXPERT' ? 'contained' : 'outlined'}
                onClick={() => handleSetMode('EXPERT')}
              >
                Mode EXPERT
              </Button>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              En mode GUIDÉ, l'élève ne pourra choisir que des combinaisons atelier ↔ muscle prévues.
              En mode EXPERT, il peut choisir librement.
            </Typography>
          </Box>

          <Box>
            <Button startIcon={<SettingsIcon />} color="primary" onClick={resetDefaults} variant="outlined">
              Réinitialiser valeurs par défaut
            </Button>
          </Box>
        </Box>

        <Divider sx={{ mb: 2 }} />

        <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          <Box sx={{ flex: 1, minWidth: 280 }}>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>Ateliers</Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <TextField value={newAtelier} onChange={e => setNewAtelier(e.target.value)} label="Nouvel atelier" size="small" fullWidth />
              <Button variant="contained" startIcon={<AddIcon />} onClick={addAtelier}>Ajouter</Button>
            </Box>
            <List dense>
              {ateliers.length === 0 && !loading ? <ListItemText primary="Aucun atelier défini" /> : null}
              {ateliers.map(a => (
                <EditableItem key={a._id || a.name} item={a} onUpdate={updateAtelier} onDelete={deleteAtelier} />
              ))}
            </List>
          </Box>

          <Box sx={{ flex: 1, minWidth: 280 }}>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>Groupes musculaires</Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <TextField value={newMuscle} onChange={e => setNewMuscle(e.target.value)} label="Nouveau muscle" size="small" fullWidth />
              <Button variant="contained" startIcon={<AddIcon />} onClick={addMuscle}>Ajouter</Button>
            </Box>
            <List dense>
              {muscles.length === 0 && !loading ? <ListItemText primary="Aucun muscle défini" /> : null}
              {muscles.map(m => (
                <EditableItem key={m._id || m.name} item={m} onUpdate={updateMuscle} onDelete={deleteMuscle} />
              ))}
            </List>
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>Mappings (muscle ↔ ateliers)</Typography>
          {mappings.length === 0 && !loading ? <Typography variant="body2" color="text.secondary">Aucun mapping défini</Typography> : null}
          <Box sx={{ display: 'grid', gap: 2 }}>
            {mappings.map((mp) => (
              <Box key={mp.muscle} sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                <Typography sx={{ minWidth: 180 }}>{mp.muscle}</Typography>
                <FormControl sx={{ minWidth: 320 }}>
                  <InputLabel id={`map-${mp.muscle}`}>Ateliers</InputLabel>
                  <Select
                    labelId={`map-${mp.muscle}`}
                    multiple
                    value={mp.ateliers || []}
                    onChange={(e) => handleChangeMapping(mp.muscle, e.target.value)}
                    input={<OutlinedInput label="Ateliers" />}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((v) => <Chip key={v} label={v} size="small" />)}
                      </Box>
                    )}
                  >
                    {atelierNames.map(a => <MenuItem key={a} value={a}>{a}</MenuItem>)}
                  </Select>
                </FormControl>
                <IconButton color="error" onClick={() => deleteMapping(mp.muscle)} title="Supprimer mapping"><DeleteIcon /></IconButton>
              </Box>
            ))}

            {muscles.map(m => {
              const exists = mappings.find(mp => mp.muscle === m.name);
              if (exists) return null;
              return (
                <Box key={`newmap-${m.name}`} sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                  <Typography sx={{ minWidth: 180 }}>{m.name}</Typography>
                  <FormControl sx={{ minWidth: 320 }}>
                    <InputLabel id={`map-new-${m.name}`}>Ateliers</InputLabel>
                    <Select
                      labelId={`map-new-${m.name}`}
                      multiple
                      value={[]}
                      onChange={(e) => handleChangeMapping(m.name, e.target.value)}
                      input={<OutlinedInput label="Ateliers" />}
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selected.map((v) => <Chip key={v} label={v} size="small" />)}
                        </Box>
                      )}
                    >
                      {atelierNames.map(a => <MenuItem key={a} value={a}>{a}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Box>
              );
            })}
          </Box>

          <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
            <Button variant="contained" onClick={saveMappings} disabled={savingMappings}>Enregistrer mappings</Button>
          </Box>
        </Box>

      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Fermer</Button>
      </DialogActions>
    </Dialog>
  );
}